import { CampaignDetailsFields } from 'Components/campaigns/CampaignDetails'
import BrandSelection from 'Components/Common/BrandSelection'
import RadioTileGroup from 'Components/input/RadioTileGroup/RadioTileGroup'
import { PRIVILEGE } from 'constants/privilege'
import { useFormik } from 'formik'
import { usePrivileges } from 'hooks/useAuth'
import CampaignCreationLayout from 'Layouts/CampaignCreationLayout'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FormFeedback, Spinner } from 'reactstrap'
import { AppDispatch, RootState } from 'store'
import { GigType, Milestone } from 'types/campaign/collaborations'
import { UserBrand } from 'types/users'
import { getErrorMessage } from 'utils/helpers'
import * as Yup from "yup";

import { gigTypes as defaultGigTypes, platforms } from 'constants/global'
import { createCampaign, createCampaignMilestone, deleteCampaignMilestone, updateCampaignDetails, updateCampaignMilestone, updateCampaignMilestones } from 'services/api/campaign'
import { markCampaignDataExpired, refetchCampaignLists } from 'slices/campaigns/reducer'
import withAccessControl from 'hoc/withAccessControl'
import AccessControl from 'wrappers/AccessControl'


import instagramPlatformLogo from "../../../assets/images/platforms/platform-instagram.png"
import youtubePlatformLogo from "../../../assets/images/platforms/platform-youtube.png"

const platformLogos = {
    ig: instagramPlatformLogo,
    yt: youtubePlatformLogo
};


export interface CampaignInfo {
    brand_id: string | number,
    brand: UserBrand | null,
    name: string,
    visibility?: string,
    goal?: string,
    type?: string,
    mode: string,
    platform: string,
    [key: string]: any
}

const CampaignInfo: React.FC<{ viewOnly?: boolean }> = ({ viewOnly = false }) => {
    const selectedAgency = useSelector((state: RootState) => state.auth.selectedAgency);
    const configurations = useSelector((state: RootState) => state.auth.configurations);
    const [loading, setLoading] = useState<boolean>(false);
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const privileges = usePrivileges();

    const { campaignId } = useParams();
    const milestonesData = useSelector((state: RootState) => state.campaignMilestones.items[campaignId!]?.milestonesData);
    const campaignMilestones = milestonesData?.milestones ?? [];
    const gigTypes = milestonesData?.gig_types ?? defaultGigTypes;
    const properties = milestonesData?.properties ? Object.values(milestonesData?.properties) : [];

    const campaignMilestone = (Array.isArray(campaignMilestones) && campaignMilestones.length > 0) ? campaignMilestones[0] : null;

    const campaignDetails = useSelector((state: RootState) => state.campaignDetails.campaigns[(campaignId ?? 'create')])
    const campaignLoading = useSelector((state: RootState) => state.campaignDetails.loading);
    const [milestone, setMilestone] = useState<Milestone | null>(campaignMilestone ?? null);
    const goalList = campaignDetails?.goal_list ?? {};
    const milestoneGigType = (gigTypes ?? []).find((gigType) => gigType.id && milestone?.gig_type_id && gigType.id === milestone.gig_type_id);


    useEffect(() => {
        setMilestone(campaignMilestone ?? null);
    }, [campaignMilestone])

    const defaultCampaignValues: CampaignInfo = {
        name: campaignDetails?.campaign?.name ?? '',
        brand_id: campaignDetails?.campaign?.brand_id ?? '',
        brand: campaignDetails?.campaign?.brand ?? null,
        mode: campaignDetails?.campaign?.mode ?? 'workflow',
        is_public: campaignDetails?.campaign?.is_public ?? '',
        goal: campaignDetails?.campaign?.goal ?? '',
        description: campaignDetails?.campaign?.description ?? '',
        platform: campaignDetails?.campaign?.platform ?? '',
        version: 2
    }




    const formFields = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: defaultCampaignValues,
        validationSchema: Yup.object({
            brand_id: Yup.number().required("Please select a brand"),
            name: Yup.string()
                .required("Campaign name field can't be empty")
                .max(100, "Maximum 100 characters are allowed"),
            is_public: Yup.string(). // 'y' or 'n'
                required("Campaign visibility is required").oneOf(['y', 'n'], "Campaign visibility is required"),
            goal: Yup.string()
                .required("Campaign goal is required")
                .oneOf(Object.keys(goalList), "Please select a valid campaign goal"),
            platform: Yup.string()
                .required("Platform is required")
                .oneOf(Object.keys(platforms), "Please select a valid campaign platform"),
        }),
        onSubmit: async (campaignData) => {
            try {
                if (!privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT) || viewOnly)
                    throw new Error("You are not authorized to perform this action!");


                if (!milestone) {
                    return toast.error("Please select campaign type to proceed!");
                }

                setLoading(true);
                const response = campaignId
                    ? await updateCampaignDetails(selectedAgency?.uuid!, campaignId, campaignData as unknown as CampaignDetailsFields)
                    : await createCampaign(selectedAgency?.uuid!, ({ ...campaignData, tier: configurations.agency_campaign_tier ?? 'basic' } as unknown as CampaignDetailsFields));
                if (!response.campaign?.uuid) throw new Error("Failed to create new campaign..");
                if (milestone) {
                    if (milestone.id) {
                        await updateCampaignMilestone(selectedAgency?.uuid!, response.campaign.uuid, String(milestone.id), milestone);
                    } else {
                        await createCampaignMilestone(selectedAgency?.uuid!, response.campaign.uuid, milestone);
                    }
                }
                dispatch(markCampaignDataExpired({ campaignId }));
                dispatch(refetchCampaignLists());

                if (campaignData.mode === 'offline') {
                    return navigate('/campaign/' + response.campaign.uuid);
                }
                navigate('/campaign/' + response.campaign.uuid + '/gigs');
            } catch (error) {
                toast.error(getErrorMessage(error, 'Failed to create new campaign'));
            } finally {
                setLoading(false)
            }
        }
    });

    const getNewMilestone = (campaignGigType: GigType) => {
        let newMilestone: any = {
            brief: '',
            gig_type_id: campaignGigType.id,
            campaign_gig_ids: [],
            sort_order: 1,
            content_media_types: { REELS: 0, FEED: 0, STORY: 0 },
            specifications: {
                do: [],
                dont: []
            },
        };
        for (let property of properties) {
            if (property.type == 'boolean') {
                newMilestone.specifications[property.key] = false;
            } else if (property.type == 'list') {
                newMilestone.specifications[property.key] = [];
            } else if (property.type == 'object_list') {
                newMilestone.specifications[property.key] = [];
            }
        }
        newMilestone.specifications.cta_influencer = true;
        newMilestone.specifications.gig_influencer = !!campaignGigType?.has_gigs;
        newMilestone.specifications.maximum_gigs = 1;

        if (campaignGigType?.slug === 'store_visit') {
            newMilestone.specifications.gig_selection = true;
        }
        return newMilestone;
    }


    const changeGigType = async (gigTypeSlug: string) => {
        const gigType = gigTypes.find((gt) => gt.slug === gigTypeSlug);
        if (!gigType) return;

        if (milestone?.id) {
            if (!campaignId || !selectedAgency?.uuid) return;
            try {
                await deleteCampaignMilestone(selectedAgency?.uuid, campaignId, String(milestone.id));
            } catch (error: any) {
                console.log(error);
                if (error?.response?.data?.error_code === 'collaborations_exists') {
                    toast.error("Campaign type cannot be changed due to active collaborations!");
                } else {
                    toast.error(getErrorMessage(error, "Failed to change campaign type!"));
                }
                return;
            }
        }
        const newMilestone = getNewMilestone(gigType);
        setMilestone(newMilestone);
    }

    useEffect(() => {
        if (milestone && formFields.values.goal) {
            if (!milestone?.specifications?.advertiser_permission_required && formFields.values?.goal === 'conversion') {
                setMilestone({ ...milestone, specifications: { ...milestone.specifications, advertiser_permission_required: true } })
            } else if (milestone?.specifications?.advertiser_permission_required && formFields.values.goal !== 'conversion') {
                setMilestone({ ...milestone, specifications: { ...milestone.specifications, advertiser_permission_required: false } })
            }
        }
    }, [milestone, formFields.values.goal])

    return (
        <CampaignCreationLayout
            activeStep='campaign-info'
            viewOnly={viewOnly}
            actionRow={
                <div className="d-flex justify-content-between mb-4">
                    <div></div>
                    <button onClick={(e) => {
                        e.preventDefault();
                        formFields.handleSubmit();
                    }} type='button' disabled={loading || !privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)} className="theme-btn theme-btn-lg btn-gradient form-continue-btn d-flex justify-content-between align-items-center">
                        <React.Fragment>
                            {
                                'Proceed to Next'
                            }
                            {
                                loading ? <Spinner size="sm" className='me-2'> Loading... </Spinner> : <i className='bx fs-4 bx-right-arrow-alt' ></i>
                            }
                        </React.Fragment>
                    </button>
                </div>
            }
        >
            {
                !viewOnly &&
                <>
                    <div className="form-group">
                        <div className="d-flex flex-column">
                            <label htmlFor="brand">Select Brand</label>
                            <span className='field-desc'>Select or add a new brand for this campaign.</span>
                        </div>
                        <BrandSelection
                            name="brand_id"
                            value={formFields.values.brand ? { value: formFields.values.brand.id, label: formFields.values.brand.name } : null}
                            onChange={(userBrand) => {
                                formFields.setFieldValue('brand_id', userBrand?.value ?? '')
                                formFields.setFieldValue('brand', userBrand ? { name: userBrand.label, id: userBrand.value } : null)
                            }}
                            onBlur={() => formFields.setFieldTouched('brand_id', true, true)}
                        />
                        <AccessControl privilege={PRIVILEGE.BRAND.BRAND.CREATE}>
                            <div className="d-flex justify-content-end mt-2">
                                <Link to='/brand/create' target="_blank" className="text-primary">+ Create Brand</Link>
                            </div>
                        </AccessControl>
                        {
                            formFields.touched.brand_id && formFields.errors.brand_id &&
                            <FormFeedback type="invalid" className='d-block'>
                                {String(formFields.errors.brand_id)}
                            </FormFeedback>
                        }
                    </div>
                    <div className="divider my-5"></div>
                    <div className="form-group">
                        <div className="d-flex flex-column">
                            <label htmlFor="campaignName">Campaign Name <span className="text-danger">*</span></label>
                            <span className='field-desc'>Enter a short name for your campaign. This is what Creators will see!</span>
                        </div>
                        <input type="text" value={formFields.values.name} onChange={(e) => formFields.setFieldValue('name', e.target.value ?? '')} id="campaignName" className="form-control" placeholder="Enter Campaign Name" />
                        {
                            formFields.touched.name && formFields.errors.name &&
                            <FormFeedback type="invalid" className='d-block'>
                                {String(formFields.errors.name)}
                            </FormFeedback>
                        }
                    </div>
                    <div className="divider my-5"></div>
                </>
            }
            <div className="form-group">
                <div className="d-flex flex-column">
                    <label htmlFor="campaignGoal">Platform <span className="text-danger">*</span></label>
                </div>
                <div className="logo-radio-tile-group">
                        {
                            Object.values(platforms).map((platform) => (
                                <div key={platform.id} className={`logo-radio-tile cursor-pointer ${formFields?.values?.platform === platform.id ? 'active' : ''}`} onClick={() => {
                                    if(campaignId || viewOnly) return;
                                    const values = {...formFields.values, platform: platform.id};
                                    if(platform.id === 'yt' && values.goal === 'conversion'){
                                        values.goal = '';
                                    }
                                    formFields.setValues(values);
                                }}>
                                    <img className='h-100' src={platformLogos[platform.id as keyof typeof platformLogos]} />
                                </div>
                            ))
                        }
                    </div>
                {
                    formFields.touched.platform && formFields.errors.platform &&
                    <FormFeedback type="invalid" className='d-block'>
                        {String(formFields.errors.platform)}
                    </FormFeedback>
                }
            </div>
            <div className="divider my-5"></div>
            <div className="form-group">
                <div className="d-flex flex-column">
                    <label htmlFor="campaignGoal">Campaign Goal <span className="text-danger">*</span></label>
                </div>
                <RadioTileGroup
                    disabled={viewOnly}
                    options={goalList ? Object.entries(goalList)
                        .filter(([key]) => !(formFields.values.platform === 'yt' && key === 'conversion'))
                        .map(([key, goal]) => ({
                            id: key,
                            label: goal.label,
                            info: goal.description
                        })) : []
                    }
                    defaultValue={formFields.values.goal}
                    onChange={(value) => formFields.setFieldValue('goal', value)}
                    name="campaignGoal"
                    selectFirstWhenNoValueProvided={false}
                />
                {
                    formFields.touched.goal && formFields.errors.goal &&
                    <FormFeedback type="invalid" className='d-block'>
                        {String(formFields.errors.goal)}
                    </FormFeedback>
                }
            </div>
            <div className="divider my-5"></div>
            <div className="form-group">
                <div className="d-flex flex-column">
                    <label htmlFor="campaignType">Campaign Type <span className="text-danger">*</span></label>
                </div>
                <RadioTileGroup
                    disabled={viewOnly}
                    defaultValue={milestoneGigType?.slug || ''}
                    onChange={changeGigType}
                    options={gigTypes.map(gigType => ({ id: gigType.slug, label: gigType.name, info: gigType.description }))}
                    selectFirstWhenNoValueProvided={false}
                    name="campaignType"
                />
            </div>
            <div className="divider my-5"></div>
            <div className="form-group">
                <div className="d-flex flex-column">
                    <label htmlFor="campaignVisibility">Campaign Visibility <span className="text-danger">*</span></label>
                </div>
                <RadioTileGroup
                    disabled={viewOnly}
                    defaultValue={formFields.values.is_public}
                    onChange={(value) => formFields.setFieldValue('is_public', value)}
                    options={[
                        { id: 'y', label: 'Visible to Matched Creators', info: 'Matching creators can apply' },
                        { id: 'n', label: 'Invite Only', info: 'Only invited creators can apply' },
                    ]}
                    selectFirstWhenNoValueProvided={false}
                    name="campaignVisibility"
                />
                {
                    formFields.touched.is_public && formFields.errors.is_public &&
                    <FormFeedback type="invalid" className='d-block'>
                        {String(formFields.errors.is_public)}
                    </FormFeedback>
                }
            </div>
        </CampaignCreationLayout>
    )
}

export default withAccessControl(CampaignInfo, PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)
